export type SportColorPalette = {
  primary: string;
  secondary: string;
  order: number;
};

export type BannerFont = {
  name: string;
  family: string;
  weight: number;
  order: number;
};

// 2026 template registry (data-only). Templates are rendered by the ONE shared
// component packages/shared/components/TemplateBanner.tsx (+ bannerTemplates.css)
// in BOTH apps — never a second renderer. `artStyle` picks the shared gpt-image-2
// background artwork generated by api/scripts/30-generate-banner-template-art.ts:
//   'a' = moody near-monochrome venue atmosphere, 'b' = abstract sport texture.
// Layout 'gradient' is asset-free (pure CSS) — the fallback aesthetic if the CDN
// is unreachable.
export type BannerTemplate = {
  name: string;
  order: number;
  layout: 'hero' | 'duotone' | 'split' | 'glass' | 'gradient';
  artStyle?: 'a' | 'b';
};

export const BANNER_FONTS: Record<string, BannerFont> = {
  bebas_neue: {
    name: "Bebas Neue",
    family: "Bebas Neue, Impact, sans-serif",
    weight: 400,
    order: 1,
  },
  montserrat: {
    name: "Montserrat",
    family: "Montserrat, sans-serif",
    weight: 800,
    order: 2,
  },
  oswald: {
    name: "Oswald",
    family: "Oswald, Impact, sans-serif",
    weight: 700,
    order: 3,
  },
  anton: {
    name: "Anton",
    family: "Anton, Impact, sans-serif",
    weight: 400,
    order: 4,
  },
  inter: {
    name: "Inter",
    family: "Inter, system-ui, sans-serif",
    weight: 800,
    order: 5,
  },
};

export const SPORT_NAMES: Record<string, string> = {
  basketball: "Basketball",
  pickleball: "Pickleball",
  soccer: "Soccer",
  golf: "Golf",
  volleyball: "Volleyball",
  softball: "Softball",
  baseball: "Baseball",
};

export const SPORT_COLOR_PALETTES: Record<string, Record<string, SportColorPalette>> = {
  basketball: {
    orange_black: { primary: "#F97316", secondary: "#18181B", order: 1 },
    red_black: { primary: "#DC2626", secondary: "#0F0F0F", order: 2 },
    purple_gold: { primary: "#7C3AED", secondary: "#F59E0B", order: 3 },
    blue_gold: { primary: "#2563EB", secondary: "#EAB308", order: 4 },
    green_white: { primary: "#059669", secondary: "#F8FAFC", order: 5 },
    royal_gold: { primary: "#1D4ED8", secondary: "#F59E0B", order: 6 },
    violet_gold: { primary: "#6D28D9", secondary: "#FBBF24", order: 7 },
    burgundy_cream: { primary: "#881337", secondary: "#FFF7ED", order: 8 },
    teal_black: { primary: "#0D9488", secondary: "#18181B", order: 9 },
    sky_navy: { primary: "#0EA5E9", secondary: "#172554", order: 10 },
  },
  pickleball: {
    lime_blue: { primary: "#84CC16", secondary: "#0284C7", order: 1 },
    yellow_navy: { primary: "#FACC15", secondary: "#1E3A8A", order: 2 },
    teal_orange: { primary: "#14B8A6", secondary: "#EA580C", order: 3 },
    navy_lime: { primary: "#1E3A8A", secondary: "#A3E635", order: 4 },
    blue_yellow: { primary: "#3B82F6", secondary: "#FDE047", order: 5 },
    blue_lime: { primary: "#2563EB", secondary: "#84CC16", order: 6 },
    teal_navy: { primary: "#0F766E", secondary: "#172554", order: 7 },
    purple_aqua: { primary: "#7C3AED", secondary: "#22D3EE", order: 8 },
    green_black: { primary: "#16A34A", secondary: "#18181B", order: 9 },
    orange_black: { primary: "#EA580C", secondary: "#18181B", order: 10 },
    cyan_navy: { primary: "#0891B2", secondary: "#172554", order: 11 },
  },
  baseball: {
    navy_red: { primary: "#1E3A8A", secondary: "#DC2626", order: 1 },
    red_navy: { primary: "#B91C1C", secondary: "#1E293B", order: 2 },
    blue_orange: { primary: "#2563EB", secondary: "#F97316", order: 3 },
    black_gold: { primary: "#18181B", secondary: "#EAB308", order: 4 },
    green_gold: { primary: "#166534", secondary: "#CA8A04", order: 5 },
    // "Navy + Red" like navy_red but deeper — key kept distinct.
    midnight_red: { primary: "#172554", secondary: "#DC2626", order: 6 },
    forest_cream: { primary: "#14532D", secondary: "#FFF7ED", order: 7 },
    royal_gold: { primary: "#1E40AF", secondary: "#D97706", order: 8 },
    scarlet_black: { primary: "#DC2626", secondary: "#18181B", order: 9 },
    royal_black: { primary: "#2563EB", secondary: "#09090B", order: 10 },
    orange_navy: { primary: "#EA580C", secondary: "#172554", order: 11 },
    copper_black: { primary: "#C2410C", secondary: "#18181B", order: 12 },
  },
  soccer: {
    green_white: { primary: "#16A34A", secondary: "#F1F5F9", order: 1 },
    red_white: { primary: "#DC2626", secondary: "#FAFAFA", order: 2 },
    blue_white: { primary: "#2563EB", secondary: "#F8FAFC", order: 3 },
    yellow_green: { primary: "#EAB308", secondary: "#15803D", order: 4 },
    black_gold: { primary: "#171717", secondary: "#F59E0B", order: 5 },
    forest_gold: { primary: "#166534", secondary: "#F59E0B", order: 6 },
    royal_white: { primary: "#1D4ED8", secondary: "#FFFFFF", order: 7 },
    crimson_navy: { primary: "#BE123C", secondary: "#172554", order: 8 },
    orange_black: { primary: "#EA580C", secondary: "#18181B", order: 9 },
  },
  volleyball: {
    blue_yellow: { primary: "#0EA5E9", secondary: "#FACC15", order: 1 },
    red_white: { primary: "#DC2626", secondary: "#FAFAFA", order: 2 },
    navy_orange: { primary: "#1E3A8A", secondary: "#F97316", order: 3 },
    teal_gold: { primary: "#0D9488", secondary: "#D97706", order: 4 },
    purple_gold: { primary: "#7C3AED", secondary: "#EAB308", order: 5 },
    royal_gold: { primary: "#1D4ED8", secondary: "#FBBF24", order: 6 },
    purple_white: { primary: "#7E22CE", secondary: "#FFFFFF", order: 7 },
    teal_navy: { primary: "#0F766E", secondary: "#172554", order: 8 },
    green_black: { primary: "#16A34A", secondary: "#18181B", order: 9 },
    sky_navy: { primary: "#0EA5E9", secondary: "#172554", order: 10 },
    orange_black: { primary: "#F97316", secondary: "#18181B", order: 11 },
  },
  softball: {
    yellow_navy: { primary: "#FACC15", secondary: "#1E3A8A", order: 1 },
    gold_black: { primary: "#F59E0B", secondary: "#18181B", order: 2 },
    red_navy: { primary: "#DC2626", secondary: "#1E293B", order: 3 },
    teal_gold: { primary: "#0F766E", secondary: "#EAB308", order: 4 },
    purple_gold: { primary: "#7C3AED", secondary: "#F59E0B", order: 5 },
    navy_gold: { primary: "#172554", secondary: "#F59E0B", order: 6 },
    violet_gold: { primary: "#6D28D9", secondary: "#FBBF24", order: 7 },
    teal_charcoal: { primary: "#0F766E", secondary: "#27272A", order: 8 },
    orange_navy: { primary: "#EA580C", secondary: "#172554", order: 9 },
    sky_black: { primary: "#0EA5E9", secondary: "#18181B", order: 10 },
  },
  golf: {
    green_gold: { primary: "#166534", secondary: "#D4AF37", order: 1 },
    navy_white: { primary: "#1E3A8A", secondary: "#F8FAFC", order: 2 },
    forest_cream: { primary: "#14532D", secondary: "#FEF3C7", order: 3 },
    teal_silver: { primary: "#0F766E", secondary: "#CBD5E1", order: 4 },
    green_navy: { primary: "#15803D", secondary: "#1E293B", order: 5 },
    forest_gold: { primary: "#14532D", secondary: "#D4A72C", order: 6 },
    navy_cream: { primary: "#172554", secondary: "#FAFAF9", order: 7 },
    burgundy_gold: { primary: "#7F1D1D", secondary: "#D4A72C", order: 8 },
    emerald_black: { primary: "#059669", secondary: "#18181B", order: 9 },
    copper_black: { primary: "#C2410C", secondary: "#18181B", order: 10 },
  },
  // Sports below aren't in the new-org picker (sportOptions.ts) yet, but
  // legacy orgs carry them (see sportLabels.ts) — without an entry here they
  // fall back to the basketball palettes. No SPORT_NAMES entry on purpose:
  // templateArtUrl would point at CDN art that script 30 never generated.
  tennis: {
    forest_cream: { primary: "#166534", secondary: "#FEFCE8", order: 1 },
    navy_lime: { primary: "#172554", secondary: "#A3E635", order: 2 },
    clay_cream: { primary: "#C2410C", secondary: "#FFF7ED", order: 3 },
    green_black: { primary: "#16A34A", secondary: "#09090B", order: 4 },
    royal_black: { primary: "#2563EB", secondary: "#09090B", order: 5 },
    orange_navy: { primary: "#EA580C", secondary: "#172554", order: 6 },
    teal_charcoal: { primary: "#0D9488", secondary: "#27272A", order: 7 },
    red_black: { primary: "#DC2626", secondary: "#0F0F0F", order: 8 },
  },
  track: {
    red_charcoal: { primary: "#DC2626", secondary: "#27272A", order: 1 },
    blue_black: { primary: "#2563EB", secondary: "#09090B", order: 2 },
    orange_navy: { primary: "#EA580C", secondary: "#172554", order: 3 },
    green_black: { primary: "#16A34A", secondary: "#09090B", order: 4 },
    purple_black: { primary: "#7C3AED", secondary: "#09090B", order: 5 },
    teal_navy: { primary: "#0D9488", secondary: "#172554", order: 6 },
  },
  judo: {
    red_black: { primary: "#B91C1C", secondary: "#09090B", order: 1 },
    indigo_white: { primary: "#3730A3", secondary: "#FFFFFF", order: 2 },
    black_gold: { primary: "#18181B", secondary: "#D4A72C", order: 3 },
    royal_black: { primary: "#2563EB", secondary: "#09090B", order: 4 },
    teal_black: { primary: "#0D9488", secondary: "#18181B", order: 5 },
    orange_black: { primary: "#EA580C", secondary: "#18181B", order: 6 },
    purple_black: { primary: "#7C3AED", secondary: "#09090B", order: 7 },
  },
};

export const BANNER_TEMPLATES: Record<string, BannerTemplate> = {
  hero: { name: "Hero", order: 1, layout: "hero", artStyle: "a" },
  duotone: { name: "Duotone", order: 2, layout: "duotone", artStyle: "b" },
  split: { name: "Split Panel", order: 3, layout: "split", artStyle: "a" },
  glass: { name: "Glass", order: 4, layout: "glass", artStyle: "a" },
  gradient: { name: "Gradient", order: 5, layout: "gradient" },
};

// Render-time migration of the retired 2016-era SVG templates (owner decision
// 2026-08-23: migrate ALL — stored legacy keys resolve to the closest new
// template, so existing custom-banner orgs modernize with no data migration).
export const LEGACY_TEMPLATE_MAP: Record<string, string> = {
  classic_bar: "gradient",
  gradient_bar: "gradient",
  corner_accent: "split",
  side_stripe: "split",
  header_block: "hero",
  bracket_frame: "glass",
};

export const resolveTemplateKey = (key?: string | null): string => {
  if (key && BANNER_TEMPLATES[key]) return key;
  if (key && LEGACY_TEMPLATE_MAP[key]) return LEGACY_TEMPLATE_MAP[key];
  return "hero";
};

// Shared gpt-image-2 background artwork (script 30). One image per sport ×
// art style; unknown sports fall back to basketball.
export const BANNER_ART_CDN = "https://staty-bucket.sfo2.cdn.digitaloceanspaces.com/banners/templates";

export const templateArtUrl = (sport: string, artStyle: 'a' | 'b'): string => {
  const s = SPORT_NAMES[sport] ? sport : "basketball";
  return `${BANNER_ART_CDN}/${s}-${artStyle}.webp`;
};

export const getColorPaletteList = (sport: string): Array<SportColorPalette & { key: string }> =>
  Object.entries(SPORT_COLOR_PALETTES[sport] || SPORT_COLOR_PALETTES.basketball)
    .map(([key, palette]) => ({ key, ...palette }))
    .sort((a, b) => a.order - b.order);

export const getBannerTemplateList = (): Array<BannerTemplate & { key: string }> =>
  Object.entries(BANNER_TEMPLATES)
    .map(([key, template]) => ({ key, ...template }))
    .sort((a, b) => a.order - b.order);

export const getBannerFontList = (): Array<BannerFont & { key: string }> =>
  Object.entries(BANNER_FONTS)
    .map(([key, font]) => ({ key, ...font }))
    .sort((a, b) => a.order - b.order);

export const getDefaultColorKey = (sport: string): string => {
  const palettes = SPORT_COLOR_PALETTES[sport] || SPORT_COLOR_PALETTES.basketball;
  const sorted = Object.entries(palettes).sort(([, a], [, b]) => a.order - b.order);
  return sorted[0]?.[0] || 'orange_black';
};

export const getDefaultTemplateKey = (): string => {
  const sorted = Object.entries(BANNER_TEMPLATES).sort(([, a], [, b]) => a.order - b.order);
  return sorted[0]?.[0] || 'hero';
};

export const getDefaultFontKey = (): string => {
  const sorted = Object.entries(BANNER_FONTS).sort(([, a], [, b]) => a.order - b.order);
  return sorted[0]?.[0] || 'bebas_neue';
};

// AI-generated banner styles. Keys are stable; the API enum mirrors these keys.
// `promptFragment` is appended to the AI prompt for that style.
export type BannerStyle = {
  name: string;
  description: string;
  promptFragment: string;
  order: number;
};

export const BANNER_STYLES: Record<string, BannerStyle> = {
  minimal: {
    name: 'Minimal',
    description: 'Clean, modern, lots of negative space',
    promptFragment: 'clean minimalist design, lots of negative space, subtle gradients, modern flat aesthetic, refined geometric shapes',
    order: 1,
  },
  photo_realistic: {
    name: 'Photo-realistic',
    description: 'Cinematic photography look',
    promptFragment: 'photorealistic cinematic photography, dramatic natural lighting, shallow depth of field, rich textures, editorial composition',
    order: 2,
  },
  illustrated: {
    name: 'Illustrated',
    description: 'Hand-drawn, painterly',
    promptFragment: 'hand-drawn illustration style, painterly brush strokes, watercolor and ink textures, artistic and warm',
    order: 3,
  },
  graphic_novel: {
    name: 'Graphic novel',
    description: 'Bold line work, comic-book energy',
    promptFragment: 'bold graphic novel illustration, strong inked line work, dramatic shadows, comic book panel energy, halftone shading',
    order: 4,
  },
  retro: {
    name: 'Retro',
    description: '70s/80s vintage vibes',
    promptFragment: '1970s and 1980s retro aesthetic, vintage color grading, halftone textures, sun-bleached tones, faded film look',
    order: 5,
  },
  neon: {
    name: 'Neon',
    description: 'Glowing cyberpunk-tinged',
    promptFragment: 'neon glow aesthetic with vibrant electric colors, dark moody background, cyberpunk influence, light trails and rim lighting',
    order: 6,
  },
};

export const getBannerStyleList = (): Array<BannerStyle & { key: string }> =>
  Object.entries(BANNER_STYLES)
    .map(([key, style]) => ({ key, ...style }))
    .sort((a, b) => a.order - b.order);

export const getDefaultStyleKey = (): string => 'minimal';


/**
 * Text color that keeps AA contrast on a solid palette color (INT-0008).
 * Relative-luminance threshold: light primaries (e.g. basketball orange
 * #F97316, ~2.8:1 against white) get ink text; dark primaries get white.
 */
export function contrastTextForHex(hex: string): string {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // WCAG contrast ratio of white text on this color: (1.0 + 0.05) / (L + 0.05).
  // White stays only while it clears AA (≥ 4.5:1) — i.e. L ≤ ~0.1833;
  // lighter primaries (orange #F97316 ≈ 0.32) flip to ink.
  const whiteRatio = 1.05 / (luminance + 0.05);
  return whiteRatio >= 4.5 ? '#ffffff' : '#111827';
}
